import React, { useEffect, useState, useCallback, useRef } from 'react'
import { message, Table, Tag, Drawer, Form, Select, Spin, DatePicker, Input, InputNumber, Button, Tooltip } from 'antd'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FaEye, FaQrcode, FaEdit, FaDownload } from 'react-icons/fa'
import { RiCloseLine } from 'react-icons/ri'
import { getDisabledTime, verifyToken } from '../../utils/helpers'
import { secureApi } from '../../Config/api'
import { GET, POST } from '../../utils/apis'
import { pageSizeOptions, translations, dateFormate, allPaths } from '../../utils/constants'
import { Search, ParkingCircle, X, LogIn, CreditCard, LogOut, CheckCircle2, Building2, SquareParking } from 'lucide-react'
import moment from 'moment'
import axios from 'axios'
import { Buttons, SearchOrganizationsParking } from '../../Components'
import { setOrganizationFilter } from '../../Redux/actions/organizationActions'
import { setUserFilter } from '../../Redux/actions/filterUsersAction'
import { SearchOutlined } from '@ant-design/icons'
import socket from '../../Config/socket'

const Parking = ({ language }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const user = useSelector(state => state?.authReducer?.user)
  const organizationDetails = useSelector(state => state?.authReducer?.organization)
  const organizationFilter = useSelector(state => state.organizationReducer.organizationFilter)
  const userFilterRedux = useSelector(state => state?.filterUsersReducer?.userFilter)
  const organizationCount = useSelector(state => state.organizationReducer?.orgSelectionCount || 0)
  const organizationFromRedux = useSelector((state) => state.organizationReducer?.allOrganizations)

  const [editForm] = Form.useForm()

  const [initialLoading, setInitialLoading] = useState(false)
  const [parkingData, setParkingData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [availabilityData, setAvailabilityData] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [viewDrawer, setViewDrawer] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [editModal, setEditModal] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [filterStatus, setFilterStatus] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [dateRange, setDateRange] = useState([])
  const [statsData, setStatsData] = useState({ entered: 0, paid: 0, exited: 0 })
  const [searchModal, setSearchModal] = useState(false)
  const searchTimerRef = useRef(null)
  // Always points at the latest fetchParkingData so the socket listener
  // (registered once) re-fetches with the current page/filters.
  const fetchDataRef = useRef(() => { })
  const [organizations, setOrganizations] = useState([])

  const { RangePicker } = DatePicker

  const organizationId = organizationDetails?.id

  useEffect(() => {
    verifyToken(history, dispatch)
    fetchParkingData()
    setOrganizations(organizationFromRedux || [])
  }, [])

  useEffect(() => {
    fetchParkingData()
  }, [currentPage, pageSize, filterStatus, startDate, endDate, organizationFilter])

  // Live updates: refresh the list whenever a parking session is created or
  // updated on the server. Registered once; uses the ref to avoid stale state.
  useEffect(() => {
    const handleLiveUpdate = () => {
      fetchDataRef.current?.()
    }

    socket.on('parking_session_created', handleLiveUpdate)
    socket.on('parking_session_updated', handleLiveUpdate)

    return () => {
      socket.off('parking_session_created', handleLiveUpdate)
      socket.off('parking_session_updated', handleLiveUpdate)
    }
  }, [])

  const fetchParkingData = async (search = searchQuery) => {
    try {
      setInitialLoading(true)

      const params = {
        page: currentPage,
        limit: pageSize,
        role: user?.role,
        organization_id: organizationId,
        organization_filter: organizationFilter,
      }

      if (search && search.trim()) {
        params.search = search.trim()
      }

      if (filterStatus) {
        params.status = filterStatus
      }

      if (startDate) {
        params.startDate = startDate
      }

      if (endDate) {
        params.endDate = endDate
      }

      const response = await secureApi.get(GET?.GET_PARKING_HISTORY, { params })
      // console.log('Parking data response:', response)
      if (response?.success) {
        const records = response?.data?.records || []
        const pagination = response?.data?.pagination || {}
        const availability = response?.data?.availability || {}
        const stats = response?.data?.stats || { entered: 0, paid: 0, exited: 0 }

        setParkingData(records)
        setFilteredData(records)
        setAvailabilityData(availability)
        setStatsData({
          entered: Number(stats?.entered || 0),
          paid: Number(stats?.paid || 0),
          exited: Number(stats?.exited || 0)
        })
        setTotalItems(pagination?.totalRecords || records.length)
      } else {
        message.error(response?.message || 'Failed to fetch parking data.')
        setParkingData([])
        setFilteredData([])
        setStatsData({ entered: 0, paid: 0, exited: 0 })
        setTotalItems(0)
      }
      setInitialLoading(false)
    } catch (error) {
      console.error('Error fetching parking data:', error)
      message.error('Error fetching parking data.')
      setParkingData([])
      setFilteredData([])
      setStatsData({ entered: 0, paid: 0, exited: 0 })
      setTotalItems(0)
      setInitialLoading(false)
    }
  }

  // Keep the ref in sync so socket-triggered refreshes use current filters
  fetchDataRef.current = fetchParkingData

  // Debounced search handler - waits 500ms after user stops typing
  const handleSearch = useCallback((query) => {
    setSearchQuery(query)

    // Clear previous timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }

    // Set new timer for debounced API call
    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(1) // Reset to first page on new search
      fetchParkingData(query)
    }, 500) // 500ms debounce delay
  }, [])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [])

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates)
    if (dates && dates?.length === 2) {
      setStartDate(dates[0].format('YYYY-MM-DD'))
      setEndDate(dates[1].format('YYYY-MM-DD'))
    } else {
      setStartDate('')
      setEndDate('')
    }
    setCurrentPage(1)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('')
    setDateRange([])
    setStartDate('')
    setEndDate('')
    setFilterStatus('')
    setCurrentPage(1)
  }

  const viewDetails = (record) => {
    setSelectedRecord(record)
    setViewDrawer(true)
  }

  const closeViewDrawer = () => {
    setSelectedRecord(null)
    setViewDrawer(false)
  }

  const handleEdit = (record) => {
    setSelectedRecord(record)
    editForm.setFieldsValue({
      vehicle_plate: record.plate_number,
      amount: record.payment_amount || 0,
      status: record.status,
      entryTime: record.entry_time ? moment(record.entry_time) : null,
      paymentTime: record.payment_time ? moment(record.payment_time) : null,
      exitTime: record.exit_time ? moment(record.exit_time) : null,
      notes: record.notes || null
    })
    setViewDrawer(false)
    setEditModal(true)
  }

  const closeEditModal = () => {
    setSelectedRecord(null)
    setEditModal(false)
    editForm.resetFields()
  }

  const handleOrganizationChange = (value) => {
    dispatch(setOrganizationFilter(value))
    dispatch(setUserFilter(value))
  }

  const handleUpdateSession = async () => {
    const isTokenValid = await verifyToken(history, dispatch)
    if (!isTokenValid) {
      message.error('Session expired. Please log in again.')
      return
    }

    try {
      await editForm.validateFields()
      setEditLoading(true)

      const values = editForm.getFieldsValue()

      const response = await secureApi.post(POST?.UPDATE_PARKING_SESSION, {
        sessionId: selectedRecord.id,
        vehicle_plate: values.vehicle_plate,
        amount: values.amount || 0,
        status: values.status,
        notes: values.notes || null,
        entryTime: values.entryTime ? values.entryTime.toISOString() : null,
        paymentTime: values.paymentTime ? values.paymentTime.toISOString() : null,
        exitTime: values.exitTime ? values.exitTime.toISOString() : null
      })

      if (response?.success) {
        message.success(translations[language]?.parkingUpdated || 'Parking session updated successfully')
        fetchParkingData()
        closeEditModal()
      } else {
        message.error(response?.message || translations[language]?.errorUpdatingParkingSession || 'Error updating parking session.')
      }
      setEditLoading(false)
    } catch (error) {
      console.error('Error updating parking session:', error)
      message.error(translations[language]?.errorUpdatingParkingSession || 'Error updating parking session.')
      setEditLoading(false)
    }
  }

  const statusOptions = [
    { label: translations[language]?.statusEntered || 'Entered', value: 'entered' },
    { label: translations[language]?.statusPaid || 'Paid', value: 'paid' },
    { label: translations[language]?.statusExited || 'Exited', value: 'exited' }
  ]

  const getStatusColor = (status) => {
    switch (status) {
      case 'exited':
        return 'green'
      case 'entered':
        return 'blue'
      case 'paid':
        return 'cyan'
      default:
        return 'default'
    }
  }

  const getStatusText = (status) => {
    const statusMap = {
      exited: translations[language]?.statusExited || 'Exited',
      entered: translations[language]?.statusEntered || 'Entered',
      paid: translations[language]?.statusPaid || 'Paid'
    }
    return statusMap[status] || status
  }

  // Open QR PDF in new tab
  const openQRCode = (qrToken) => {
    history.push(`/parking/qr/${qrToken}`);
  }

  const viewParkingLots = () => {
    if (!organizationId) {
      message.error(translations[language]?.organizationRequired || 'Organization is required.')
      return
    }

    const url = `${allPaths?.PARKING_SLOTS}/${organizationId}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const downloadPDF = async (record) => {
    if (!record?.id) {
      message.error('Session ID not found')
      return
    }

    const isTokenValid = await verifyToken(history, dispatch)
    if (!isTokenValid) {
      message.error('Session expired. Please log in again.')
      return
    }

    try {
      message.loading({ content: translations[language]?.downloading || 'Generating PDF...', key: 'pdf_download' })
      const res = await axios.post(POST?.DOWNLOAD_SINGLE_PARKING_PDF, {
        id: record.id,
        language: language || 'en'
      })

      if (res?.data?.success && res?.data?.data) {
        const pdfUrl = res.data.data
        const link = document.createElement('a')
        link.href = pdfUrl
        link.download = `Receipt_${record.session_code || record.id}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        message.success({
          content: translations[language]?.downloadSuccess || 'Downloaded successfully',
          key: 'pdf_download',
          duration: 2
        })
      } else {
        message.error({
          content: res?.data?.message || (translations[language]?.downloadError || 'Failed to download PDF'),
          key: 'pdf_download',
          duration: 2
        })
      }
    } catch (error) {
      console.error('Download single PDF error:', error)
      message.error({
        content: translations[language]?.downloadError || 'Failed to download PDF',
        key: 'pdf_download',
        duration: 2
      })
    }
  }

  const columns = [
    {
      title: translations[language]?.plateNumber || 'Plate Number',
      dataIndex: 'plate_number',
      key: 'plate_number',
      width: 120,
      render: (text) => text ? <span className='vl-plate capitalized-name'>{text}</span> : <span className='vl-muted'>-</span>
    },
    {
      title: translations[language]?.sessionCode || 'Session Code',
      dataIndex: 'session_code',
      key: 'session_code',
      width: 200,
      render: (text) => text ? <span className='vl-plate'>{text}</span> : <span className='vl-muted'>-</span>
    },
    {
      title: translations[language]?.amount || 'Amount',
      dataIndex: 'payment_amount',
      key: 'payment_amount',
      width: 100,
      render: (text, record) => {
        const amount = text || 0
        const currency = record.currency || 'EUR'
        return <strong style={{ whiteSpace: 'nowrap' }}>{currency === 'EUR' ? `€${parseFloat(amount).toFixed(2)}` : `${parseFloat(amount).toFixed(2)} ${currency}`}</strong>
      }
    },
    {
      title: translations[language]?.entryTime || 'Entry Time',
      dataIndex: 'entry_time',
      key: 'entry_time',
      width: 150,
      render: (text) => text ? <span style={{ whiteSpace: 'nowrap' }}>{moment(text).format('DD/MM/YYYY HH:mm')}</span> : <span className='vl-muted'>-</span>
    },
    {
      title: translations[language]?.paymentTime || 'Payment Time',
      dataIndex: 'payment_time',
      key: 'payment_time',
      width: 150,
      render: (text) => text ? <span style={{ whiteSpace: 'nowrap' }}>{moment(text).format('DD/MM/YYYY HH:mm')}</span> : <span className='vl-muted'>-</span>
    },
    {
      title: translations[language]?.exitTime || 'Exit Time',
      dataIndex: 'exit_time',
      key: 'exit_time',
      width: 150,
      render: (text) => text ? <span style={{ whiteSpace: 'nowrap' }}>{moment(text).format('DD/MM/YYYY HH:mm')}</span> : <span className='vl-muted'>-</span>
    },
    ...(user?.role === 'superAdmin' ? [{
      title: translations[language]?.organization || 'Organization',
      dataIndex: 'organization_name',
      key: 'organization_name',
      width: 150,
      render: (text) => text ? <span style={{ whiteSpace: 'nowrap' }}>{text}</span> : <span className='vl-muted'>-</span>
    }] : []),
    {
      title: translations[language]?.status || 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)} style={{ margin: 0 }}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: translations[language]?.actions || 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <div className='vl-actions'>
          <Tooltip title={translations[language]?.view || 'View'}>
            <button
              type='button'
              className='vl-action-btn vl-action-view'
              onClick={() => viewDetails(record)}
            >
              <FaEye size={14} />
            </button>
          </Tooltip>
          <Tooltip title={translations[language]?.edit || 'Edit'}>
            <button
              type='button'
              className='vl-action-btn vl-action-edit'
              onClick={() => handleEdit(record)}
            >
              <FaEdit size={14} />
            </button>
          </Tooltip>
          {record.qr_token && (
            <Tooltip title={translations[language]?.downloadPDF}>
              <button
                type='button'
                className='vl-action-btn vl-action-edit'
                onClick={() => downloadPDF(record)}
              >
                <FaDownload size={14} />
              </button>
            </Tooltip>
          )}
        </div>
      )
    }
  ]

  const showSearchModal = () => {
    setSearchModal(!searchModal)
  }

  return (
    <div className='main-container'>
      <div className='main-inner-container'>
        <div className='insider-container-buttons report-header-div'>
          <h1 className='main-heading'>{translations[language]?.parkingSystem || 'Parking System'}</h1>
          {user?.role === 'admin' && (
            <button className='add-button' onClick={viewParkingLots}>
              <SquareParking className='icon' size={16} />
              {translations[language]?.viewParkingLots || 'View Parking Lots'}
            </button>
          )}
        </div>

        {/* Stats Cards */}
        <div className='vd-stats' style={{ marginBottom: 24 }}>
          {[
            { key: 'entered', label: translations[language]?.totalEntered || 'Total Entered', value: statsData.entered, color: '#1976D2', icon: <LogIn size={22} color='#fff' /> },
            { key: 'paid', label: translations[language]?.totalPaid || 'Total Paid', value: statsData.paid, color: '#00B8D9', icon: <CreditCard size={22} color='#fff' /> },
            { key: 'exited', label: translations[language]?.totalExited || 'Total Exited', value: statsData.exited, color: '#22c55e', icon: <LogOut size={22} color='#fff' /> },
            { key: 'totalSpots', label: translations[language]?.totalSpots || 'Total Spots', value: availabilityData?.totalSpots || 0, color: '#7c3aed', icon: <ParkingCircle size={22} color='#fff' /> },
            { key: 'occupiedSpots', label: translations[language]?.occupiedSpots || 'Occupied Spots', value: availabilityData?.occupiedSpots || 0, color: '#f59e0b', icon: <LogIn size={22} color='#fff' /> },
            { key: 'availableSpots', label: translations[language]?.availableSpots, value: availabilityData?.freeSpots || 0, color: '#16a34a', icon: <CheckCircle2 size={22} color='#fff' /> }
          ].map((c) => (
            <div className='vd-stat' key={c.key}>
              <div className='vd-stat-icon' style={{ backgroundColor: c.color }}>{c.icon}</div>
              <div>
                <p className='vd-stat-value'>{c.value}</p>
                <p className='vd-stat-label'>{c.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className='insider-container'>
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              gap: 12,
              flexWrap: 'nowrap',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <Input
              placeholder={translations[language]?.searchParking || 'Search by plate number...'}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: 200, height: 40, borderRadius: 6, borderWidth: 1, borderStyle: 'solid', borderColor: '#d9d9d9', boxSizing: 'border-box' }}
              prefix={<SearchOutlined />}
              allowClear
            />
            <div style={{ width: '200px !important' }}>
              <RangePicker
                className='ip-address-date-picker'
                format={dateFormate}
                value={dateRange} style={{ width: 250, height: 40, borderRadius: 6, borderWidth: 1, borderStyle: 'solid', borderColor: '#d9d9d9', boxSizing: 'border-box' }}

                onChange={handleDateRangeChange}
                disabledDate={(current) => current && current > moment().endOf('day')}
                placeholder={[
                  translations[language]?.startDate || 'Start Date',
                  translations[language]?.endDate || 'End Date'
                ]}
                allowClear
              />
            </div>
            {(searchQuery || dateRange?.length > 0 || filterStatus) && (
              <button
                onClick={clearFilters}
                className='organization close-organization'
              >
                <X size={16} />
                {translations[language]?.clearFilters || 'Clear Filters'}
              </button>
            )}

            {user?.role === 'superAdmin' && (
              <div className='get-reports-container'>
                <span
                  className={`organization ${searchModal ? 'open-organization' : 'close-organization'}`}
                  onClick={showSearchModal}
                >
                  <Building2 />

                  {translations[language]?.organizations || 'Organizations'}

                  {organizationCount ? (
                    <div className='filter-count-badge'>
                      {organizationCount}
                    </div>
                  ) : null}
                </span>
              </div>
            )}
          </div>
          <div className='vd-card'>
            {parkingData?.length === 0 && !initialLoading ? (
              <div className='vd-card-body'>
                <div className="no-vehicles-container">
                  <div className="no-vehicles-text-center">
                    <div className="flex-col-center">
                      <div className="no-vehicles-text-center">
                        <ParkingCircle className='no-vehicle-icon' />
                        <h3 className="no-vehicles-heading">{translations[language]?.noParkingFound || 'No Parking Records Found'}</h3>
                        <p className="no-vehicles-description">
                          {translations[language]?.noParkingHistory || 'No parking history available yet'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className='vd-card-header'>
                  <div className='vl-list-title'>
                    <h2 className='vd-card-title'>{translations[language]?.parkingList || 'Parking List'}</h2>
                    <span className='vl-total-chip'>{translations[language]?.totalRecord || 'Total Records'}: <strong>{totalItems}</strong></span>
                  </div>
                </div>
                <div className='vd-card-body'>
                  <Table
                    className='vl-table'
                    dataSource={filteredData}
                    columns={columns}
                    rowKey="id"
                    pagination={{
                      current: currentPage,
                      pageSize: pageSize,
                      total: totalItems,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      pageSizeOptions,
                      onChange: (page, size) => {
                        setCurrentPage(page)
                        setPageSize(size)
                      }
                    }}
                    scroll={{ x: 1100 }}
                    loading={initialLoading}
                    size="middle"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <Drawer
        title={<span className="capitalized-name">{translations[language]?.parkingDetails || 'Parking Details'}</span>}
        visible={viewDrawer}
        onClose={closeViewDrawer}
        width={750}
        placement='right'
        closable={true}
        className='custom-drawer'
        closeIcon={<span className='custom-close-icon'><RiCloseLine /></span>}
        style={{ zIndex: 1000 }}
      >
        {selectedRecord && (
          <div className='custom-modal-inner-container'>
            <div className='information-container'>
              <h3 className='info-main-heading'>{translations[language]?.parkingDetails || 'Parking Details'}</h3>

              <div className='inner-information-div'>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.plateNumber || 'Plate Number'}</h3>
                    <p className='capitalized-name'>{selectedRecord.plate_number || '-'}</p>
                  </div>
                </div>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.sessionCode || 'Session Code'}</h3>
                    <p>{selectedRecord.session_code || '-'}</p>
                  </div>
                </div>
              </div>

              <div className='inner-information-div'>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.amount || 'Amount'}</h3>
                    <p>{selectedRecord.currency === 'EUR' ? '€' : selectedRecord.currency}{parseFloat(selectedRecord.payment_amount || 0).toFixed(2)}</p>
                  </div>
                </div>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.status || 'Status'}</h3>
                    <Tag color={getStatusColor(selectedRecord.status)} style={{ marginTop: '5px' }}>
                      {getStatusText(selectedRecord.status)}
                    </Tag>
                  </div>
                </div>
              </div>

              <div className='inner-information-div'>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.entryTime || 'Entry Time'}</h3>
                    <p>{selectedRecord.entry_time ? moment(selectedRecord.entry_time).format('DD/MM/YYYY HH:mm') : '-'}</p>
                  </div>
                </div>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.entryGate || 'Entry Gate'}</h3>
                    <p>{selectedRecord.entry_gate_id || '-'}</p>
                  </div>
                </div>
              </div>

              <div className='inner-information-div'>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.paymentTime || 'Payment Time'}</h3>
                    <p>{selectedRecord.payment_time ? moment(selectedRecord.payment_time).format('DD/MM/YYYY HH:mm') : '-'}</p>
                  </div>
                </div>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.paymentProvider || 'Payment Provider'}</h3>
                    <p>{selectedRecord.payment_provider || '-'}</p>
                  </div>
                </div>
              </div>

              <div className='inner-information-div'>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.exitTime || 'Exit Time'}</h3>
                    <p>{selectedRecord.exit_time ? moment(selectedRecord.exit_time).format('DD/MM/YYYY HH:mm') : '-'}</p>
                  </div>
                </div>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.exitGate || 'Exit Gate'}</h3>
                    <p>{selectedRecord.exit_gate_id || '-'}</p>
                  </div>
                </div>
              </div>

              {selectedRecord.notes && (
                <div className='inner-information-div'>
                  <div className='info-childern-div' style={{ width: '100%' }}>
                    <div className='inner-information-childern'>
                      <h3 className='info-heading'>{translations[language]?.notes || 'Notes'}</h3>
                      <p>{selectedRecord.notes}</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedRecord.qr_token && (
                <div className='drawer-action-buttons' style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className='add-button'
                    onClick={() => openQRCode(selectedRecord?.qr_token)}                  >
                    <FaQrcode style={{ marginRight: '8px' }} />
                    {translations[language]?.viewQRCode || 'View QR Code'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>

      {/* Edit Modal */}
      <Drawer
        title={<span className="capitalized-name">{translations[language]?.editParking || 'Edit Parking Session'}</span>}
        visible={editModal}
        onClose={closeEditModal}
        width={750}
        placement='right'
        closable={true}
        className='custom-drawer'
        closeIcon={<span className='custom-close-icon'><RiCloseLine /></span>}
        style={{ zIndex: 1001 }}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button
              key="cancel"
              onClick={closeEditModal}
              type="default"
              className="drawer-cancel"
            >
              {translations[language]?.cancelButton || 'Cancel'}
            </Button>

            <Buttons
              key="action"
              onClick={handleUpdateSession}
              type="primary"
              loading={editLoading}
              className="Delete-button"
            >
              {translations[language]?.updateParking || 'Update'}
            </Buttons>
          </div>
        }
      >
        <Spin spinning={editLoading}>
          <Form form={editForm} layout="vertical">
            <Form.Item
              label={translations[language]?.sessionCode || 'Session Code'}
              style={{ marginBottom: 16 }}
            >
              <Input
                value={selectedRecord?.session_code || '-'}
                disabled
                className='inp-height-43'
                style={{ cursor: 'not-allowed' }}
              />
            </Form.Item>

            <Form.Item
              name="vehicle_plate"
              label={translations[language]?.plateNumber || 'Plate Number'}
              rules={[{ required: true, message: translations[language]?.plateRequired || 'Please enter plate number' }]}
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder={translations[language]?.enterPlateNumber || 'Enter plate number'}
                className='inp-height-43'
              />
            </Form.Item>

            <Form.Item
              name="amount"
              label={translations[language]?.amount || 'Amount'}
              style={{ marginBottom: 16 }}
            >
              <InputNumber
                placeholder={translations[language]?.enterAmount || 'Enter amount'}
                className='inp-height-43'
                style={{ width: '100%', height: 40 }}
                min={0}
                precision={2}
                addonBefore="€"
              />
            </Form.Item>

            <Form.Item
              name="status"
              label={translations[language]?.status || 'Status'}
              rules={[{ required: true, message: translations[language]?.statusRequired || 'Please select status' }]}
              style={{ marginBottom: 16 }}
            >
              <Select
                placeholder={translations[language]?.selectStatus || 'Select Status'}
                options={statusOptions}
                className="inp-height-43 custom-select-field"
                // className='custom-inputs'
                // style={{ width: '100%', height: 40 }}
              />
            </Form.Item>

            <Form.Item
              name="entryTime"
              label={translations[language]?.entryTime || 'Entry Time'}
              style={{ marginBottom: 16 }}
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="DD/MM/YYYY HH:mm"
                style={{ width: '100%' }}
                placeholder={translations[language]?.selectEntryTime || 'Select entry time'}
                className='inp-height-43 custom-date-picker'
                disabledDate={(current) => current && current > moment().endOf('day')}
                disabledTime={getDisabledTime}
              />
            </Form.Item>

            <Form.Item
              name="paymentTime"
              label={translations[language]?.paymentTime || 'Payment Time'}
              style={{ marginBottom: 16 }}
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="DD/MM/YYYY HH:mm"
                style={{ width: '100%' }}
                placeholder={translations[language]?.selectPaymentTime || 'Select payment time'}
                className='inp-height-43 custom-date-picker'
                disabledDate={(current) => current && current > moment().endOf('day')}
                disabledTime={getDisabledTime}
              />
            </Form.Item>

            <Form.Item
              name="exitTime"
              label={translations[language]?.exitTime || 'Exit Time'}
              style={{ marginBottom: 16 }}
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="DD/MM/YYYY HH:mm"
                style={{ width: '100%' }}
                placeholder={translations[language]?.selectExitTime || 'Select exit time'}
                className='inp-height-43 custom-date-picker'
                disabledDate={(current) => current && current > moment().endOf('day')}
                disabledTime={getDisabledTime}
              />
            </Form.Item>

            <Form.Item
              name="notes"
              label={translations[language]?.notes || 'Notes'}
              style={{ marginBottom: 16 }}
            >
              <Input.TextArea
                rows={3}
                placeholder={translations[language]?.enterNotes || 'Enter notes'}
                style={{ maxHeight: 200, overflowY: 'auto' }}
              />
            </Form.Item>
          </Form>
        </Spin>
      </Drawer>
      <div className={`search-organization-main-div ${searchModal ? 'open' : ''}`}>
        <SearchOrganizationsParking
          searchType={"parking"}
          selectedOrganizations={organizationFilter}
          handleOrganizationChange={handleOrganizationChange}
          organizations={organizations}
          setSearchModal={setSearchModal}
          searchModal={searchModal}
          selectedFilter={userFilterRedux}
          language={language}
        />
      </div>
    </div>
  )
}

export default Parking
